/**
 * ImportJobs — Historique des imports batch (ventes + achats) + alertes
 * post-import.
 *
 * Structure :
 *   - Liste des 100 derniers jobs (tri desc)
 *   - Sélection d'un job → détail + alertes rattachées
 *   - Alertes dismissable individuellement
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { AlertTriangle, CheckCircle2, XCircle, Loader2, Filter, Trash2 } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = { ventes: "Ventes", achats: "Achats" };
const STATUS_STYLE = {
  processing: "bg-blue-50 text-blue-700 border-blue-200",
  done:       "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled:  "bg-slate-50 text-slate-600 border-slate-200",
  pending:    "bg-slate-50 text-slate-600 border-slate-200",
  uploading:  "bg-amber-50 text-amber-700 border-amber-200",
};
const STATUS_LABEL = { done: "Terminé", processing: "En cours", cancelled: "Annulé", pending: "En attente", uploading: "Envoi en cours" };

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR",
      { day: "2-digit", month: "2-digit", year: "numeric",
         hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function ImportJobs({ embedded = false } = {}) {
  const [params, setParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterKind, setFilterKind] = useState("all");

  const jobId = params.get("job");

  const loadJobs = async () => {
    setLoading(true);
    try {
      const r = await api.get("/import-jobs");
      setJobs(r.data.jobs || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const loadJobDetail = async (id) => {
    try {
      const [rj, ra] = await Promise.all([
        api.get(`/import-jobs/${id}`),
        api.get(`/import-alerts?job_id=${id}&dismissed=false`),
      ]);
      setSelectedJob(rj.data.job);
      setAlerts(ra.data.alerts || []);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { loadJobs(); }, []);
  useEffect(() => {
    if (jobId) loadJobDetail(jobId);
    else { setSelectedJob(null); setAlerts([]); }
    // eslint-disable-next-line
  }, [jobId]);

  const filtered = useMemo(() => {
    if (filterKind === "all") return jobs;
    return jobs.filter((j) => j.kind === filterKind);
  }, [jobs, filterKind]);

  const dismissAlert = async (aid) => {
    try {
      await api.post(`/import-alerts/${aid}/dismiss`);
      setAlerts((prev) => prev.filter((a) => a.id !== aid));
      toast.success("Alerte ignorée");
    } catch (e) { toast.error(apiError(e)); }
  };

  const openJob = (id) => setParams({ job: id });
  const closeJob = () => setParams({});

  return (
    <div className="space-y-6" data-testid="import-jobs-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          {!embedded && (
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
                style={{ fontFamily: "Manrope, sans-serif" }}>
              Historique des imports
            </h1>
          )}
          <p className={embedded ? "hidden" : "mt-1 text-sm text-slate-500"}>
            Suivi des imports en masse (ventes + achats) et alertes post-import.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/app/invoices/import-batch">
            <Button variant="outline" size="sm" data-testid="new-ventes-import-btn">
              + Import ventes
            </Button>
          </Link>
          <Link to="/app/purchases/import-batch">
            <Button variant="outline" size="sm" data-testid="new-achats-import-btn">
              + Import achats
            </Button>
          </Link>
        </div>
      </div>

      {!selectedJob ? (
        <>
          <div className="flex items-center gap-2 text-sm">
            <Filter className="h-4 w-4 text-slate-400" />
            <span className="text-slate-500">Filtrer :</span>
            {["all", "ventes", "achats"].map((k) => (
              <button key={k} onClick={() => setFilterKind(k)}
                       className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                         filterKind === k
                           ? "bg-blue-600 text-white border-blue-600"
                           : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                       data-testid={`filter-${k}-btn`}>
                {k === "all" ? "Tous" : KIND_LABEL[k]}
              </button>
            ))}
          </div>

          <Card>
            {loading ? (
              <div className="p-12 text-center text-sm text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Chargement…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center text-sm text-slate-400"
                    data-testid="empty-state">
                Aucun job d'import pour le moment.
              </div>
            ) : (
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead>Créé le</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Utilisateur</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Fichiers</TableHead>
                  <TableHead className="text-right">Succès</TableHead>
                  <TableHead className="text-right">Erreurs</TableHead>
                  <TableHead className="text-right">Alertes</TableHead>
                  <TableHead />
                </TableRow></TableHeader>
                <TableBody>
                  {filtered.map((j) => (
                    <TableRow key={j.id} data-testid={`job-row-${j.id}`}
                                className="hover:bg-slate-50/60">
                      <TableCell className="text-xs text-slate-600">
                        {fmtDate(j.created_at)}
                      </TableCell>
                      <TableCell>
                      <Badge variant="outline"
                            className={j.kind === "ventes"
                              ? "bg-blue-50 text-blue-700 border-blue-200"
                              : "bg-indigo-50 text-indigo-700 border-indigo-200"}>
                      {KIND_LABEL[j.kind] || j.kind}
                    </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {j.created_by_email || "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline"
                                className={STATUS_STYLE[j.status] || STATUS_STYLE.pending}>
                          {STATUS_LABEL[j.status] || j.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">{j.total}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-emerald-700">
                        {j.success || 0}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs text-red-700">
                        {j.error || 0}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {j.alerts_count > 0 ? (
                          <span className="text-amber-700 font-semibold">
                            ⚠ {j.alerts_count}
                          </span>
                        ) : "—"}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => openJob(j.id)}
                                 data-testid={`open-job-${j.id}-btn`}>
                          Ouvrir →
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </>
      ) : (
        <JobDetail job={selectedJob} alerts={alerts}
                    onClose={closeJob} onDismissAlert={dismissAlert} />
      )}
    </div>
  );
}

function JobDetail({ job, alerts, onClose, onDismissAlert }) {
  return (
    <div className="space-y-4">
      <button className="text-sm text-slate-500 hover:text-slate-900"
              onClick={onClose} data-testid="job-detail-back-btn">
        ← Retour à la liste
      </button>

      <Card><CardContent className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">
              Job {job.kind} · {fmtDate(job.created_at)}
            </div>
            <div className="text-lg font-semibold text-slate-900 mt-1">
              {job.total} fichier(s) — {job.success || 0} succès, {job.error || 0} erreurs
            </div>
          </div>
          <Badge variant="outline" className={STATUS_STYLE[job.status]}>
            {STATUS_LABEL[job.status] || job.status}
          </Badge>
        </div>
      </CardContent></Card>

      {alerts.length > 0 && (
        <Card>
          <div className="p-4 border-b bg-amber-50/50">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              <AlertTriangle className="h-4 w-4" />
              {alerts.length} alerte(s) post-import à vérifier
            </div>
          </div>
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Type</TableHead>
              <TableHead>Message</TableHead>
              <TableHead>Sévérité</TableHead>
              <TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {alerts.map((a) => (
                <TableRow key={a.id} data-testid={`alert-row-${a.id}`}>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {a.kind}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">
                    <Link to={
                      job.kind === "ventes"
                        ? `/app/invoices/${a.invoice_id}/edit`
                        : `/app/purchases/supplier-invoices/${a.invoice_id}`
                    } className="hover:text-blue-600 hover:underline">
                      {a.message}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      a.severity === "warning"
                        ? "bg-amber-50 text-amber-800 border-amber-200"
                        : "bg-slate-50 text-slate-600"}>
                      {({warning:"Avertissement",info:"Info",error:"Erreur",critical:"Critique"}[a.severity]) || a.severity}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm"
                             onClick={() => onDismissAlert(a.id)}
                             data-testid={`dismiss-alert-${a.id}-btn`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Ignorer
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Card>
        <div className="p-4 border-b">
          <div className="text-sm font-semibold text-slate-900">Détail par fichier</div>
        </div>
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Fichier</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead>N° extrait</TableHead>
            <TableHead>Détail</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {job.items.map((it, i) => (
              <TableRow key={i}>
                <TableCell className="text-sm truncate max-w-xs">{it.filename}</TableCell>
                <TableCell>
                  {it.status === "success" && (
                    <Badge variant="outline"
                            className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      <CheckCircle2 className="h-3 w-3 mr-1 inline" /> Créé
                    </Badge>
                  )}
                  {it.status === "error" && (
                    <Badge variant="outline"
                            className="bg-red-50 text-red-700 border-red-200">
                      <XCircle className="h-3 w-3 mr-1 inline" /> Échec
                    </Badge>
                  )}
                  {it.status === "processing" && (
                    <Badge variant="outline"
                            className="bg-blue-50 text-blue-700 border-blue-200">
                      <Loader2 className="h-3 w-3 mr-1 animate-spin inline" /> En cours
                    </Badge>
                  )}
                  {it.status === "pending" && <Badge variant="outline">En attente</Badge>}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {it.extracted_number || "—"}
                </TableCell>
                <TableCell className="text-xs">
                  {it.invoice_id && (
                    <Link to={
                      job.kind === "ventes"
                        ? `/app/invoices/${it.invoice_id}/edit`
                        : `/app/purchases/supplier-invoices/${it.invoice_id}`
                    } className="text-blue-600 hover:underline">Ouvrir</Link>
                  )}
                  {it.error && <span className="text-red-600">{it.error}</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
